import { estimateLeg } from './format';
import type { Waypoint } from './types';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Great-circle distance in meters. */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const R = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Distance and a rough drive time from here to there, plus the clock time you'd get in. */
export function eta(from: LatLng, to: LatLng, now = new Date()) {
  const leg = estimateLeg(from, to);
  const arrive = new Date(now.getTime() + leg.minutes * 60_000);
  return { ...leg, arrive: arrive.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) };
}

/** A ring polygon around a point, for the planner's radius circle. */
export function circlePolygon(center: LatLng, radiusMeters: number, steps = 72): [number, number][] {
  const ring: [number, number][] = [];
  const dLat = radiusMeters / 111_320;
  const dLng = radiusMeters / (111_320 * Math.cos((center.lat * Math.PI) / 180));
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    ring.push([center.lng + dLng * Math.cos(t), center.lat + dLat * Math.sin(t)]);
  }
  return ring;
}

/** A map waypoint: lettered A, B, C... unless it carries its own tag (S and F for a trip's start and finish). */
export type MapWaypoint = Waypoint & { tag?: string };

/** The dashed trip line: start, the stops in order, then the finish (or back to stop 1 for a loop). */
export function tripLine(stops: LatLng[], from?: LatLng | null, to?: LatLng | null, isLoop?: boolean): LatLng[] {
  if (!stops.length) return [];
  const pts = [...(from ? [from] : []), ...stops, ...(to ? [to] : [])];
  if (isLoop && !from && !to && stops.length > 1) pts.push(stops[0]);
  return pts.length > 1 ? pts : [];
}
